import { DtoEnvelopeTestSuggestion } from '@/api/generated-catch-backend/model';
import { ROUTE_TESTING_PLAN, ROUTE_TEST_DETAILS } from '@/constants/routes';
import useAnalyticsCalls, { DashboardSource } from '@/hooks/use-analytics';
import { getRoute } from '@/utils';
import { AnimatePresence, motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { TestSuggestionCard } from './components/testing-suggestion-card';

interface TestingPlanProps {
  testSuggestions?: DtoEnvelopeTestSuggestion;
}

export default function TestingPlan({ testSuggestions }: TestingPlanProps) {
  const navigate = useNavigate();
  const { setNavigationSource } = useAnalyticsCalls();

  if (!testSuggestions) {
    return (
      <div className="risk-cards-container">
        <div className="flex flex-col items-center gap-5">
          <div className="w-full">
            <div className="flex flex-col gap-2">
              <div className="h-7 w-48 animate-pulse rounded-full bg-gray-300" />
              <div className="h-5 w-full animate-pulse rounded-full bg-gray-200" />
            </div>
          </div>
          <div className="flex w-full gap-3 overflow-x-auto pt-2 pb-2">
            {[1, 2, 3].map((index) => (
              <div
                key={`skeleton-${index}`}
                className="relative h-[177px] w-[135px] rounded-[24px] border border-[#ededed] bg-white p-3 md:w-[208px]"
              >
                <div className="flex h-full flex-col">
                  <div className="mb-4 h-8 w-8 animate-pulse rounded bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-5 w-28 animate-pulse rounded bg-gray-200" />
                    <div className="space-y-1">
                      <div className="h-3 w-full animate-pulse rounded bg-gray-200" />
                      <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
                    </div>
                  </div>
                </div>
                <div className="absolute top-2.5 right-3 h-10 w-10 animate-pulse rounded-full bg-gray-200" />
              </div>
            ))}
          </div>
          <div className="w-full max-w-[335px]">
            <div className="h-10 w-full animate-pulse rounded-full bg-gray-300" />
          </div>
        </div>
      </div>
    );
  }

  const topTests = testSuggestions.data.slice(0, 3);
  const areAllTestsCompleted = topTests.length === 0;

  // Helper function to render icon based on test.icon data

  return (
    <div className="risk-cards-container">
      <h2 className="mobile-h3-primary text-primary action-item-text-width text-left font-bold">Your Testing Plan</h2>
      <AnimatePresence mode="wait">
        <motion.p
          className="body-regular text-primary action-item-text-width text-left"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {areAllTestsCompleted
            ? 'You’ve finished all of your tests.'
            : 'These are the top tests we recommend based on your unique risk.'}
        </motion.p>
      </AnimatePresence>

      <AnimatePresence mode="wait">
        <motion.div
          className="flex flex-col items-center gap-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <div className="flex w-full gap-3 overflow-x-auto pt-2 pb-2">
            {topTests.map((test, index) => (
              <TestSuggestionCard
                key={test.slug}
                onClick={() => {
                  setNavigationSource(DashboardSource.DASHBOARD_SPECIFIC_TEST);
                  navigate(
                    getRoute(ROUTE_TEST_DETAILS, {
                      idTest: test.recommendationId,
                    }),
                  );
                }}
                test={test}
              />
            ))}
          </div>

          <div className="cta-button-container">
            {areAllTestsCompleted ? (
              <button
                onClick={() => {
                  setNavigationSource(DashboardSource.DASHBOARD_TEST_CTA);
                  navigate(`${ROUTE_TESTING_PLAN}?tab=completed`);
                }}
                className="cta-button body-meta"
              >
                See Completed Tests
              </button>
            ) : (
              <button
                onClick={() => {
                  setNavigationSource(DashboardSource.DASHBOARD_TEST_CTA);
                  navigate(ROUTE_TESTING_PLAN);
                }}
                className="cta-button body-meta"
              >
                See All Tests
              </button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
